import type { ChangeEvent } from 'react';
import { forwardRef, useId, useRef, useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import TextField from '@mui/material/TextField';
import type { KeyboardTextFieldProps } from './KeyboardTextField.types';

export const KeyboardTextField = forwardRef<HTMLInputElement, KeyboardTextFieldProps>(function KeyboardTextField(
  { value, onChange, keyboardComponent: KeyboardComponent, changeKeyboardVisibility, id: idProp, ...rest },
  ref
) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const uuid = `keyboard-${id.replace(/:/g, '-')}`;

  const [showKeyboard, setShowKeyboard] = useState(false);
  const keyboardRef = useRef<{ setInput: (input: string) => void } | null>(null);

  const displayValue = value === null || value === undefined ? '' : String(value);

  const hide = () => {
    setShowKeyboard(false);
    changeKeyboardVisibility?.(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange(newValue);
    keyboardRef.current?.setInput(newValue);
  };

  const handleKeyboardChange = (newValue: string) => {
    onChange(newValue);
  };

  const textField = (
    <TextField
      id={id}
      {...rest}
      value={displayValue}
      onChange={handleChange}
      onFocus={() => setShowKeyboard(true)}
      onClick={() => changeKeyboardVisibility?.(true)}
      inputRef={ref ?? rest.inputRef}
    />
  );

  if (!KeyboardComponent) {
    return textField;
  }

  return (
    <ClickAwayListener onClickAway={hide}>
      <div>
        {textField}
        <div
          style={{
            display: showKeyboard ? 'inherit' : 'none',
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            zIndex: 10000,
          }}
        >
          <KeyboardComponent
            uuid={uuid}
            setShowKeyboard={setShowKeyboard}
            onChangeKeyboard={handleKeyboardChange}
            keyboardRef={keyboardRef}
          />
        </div>
      </div>
    </ClickAwayListener>
  );
});
